import { useCallback, useEffect, useRef, useState } from 'react'

/** Breathing room between revealed content and the bar, in CSS pixels. */
const REVEAL_GAP = 16

/**
 * The element that scrolls the page the bar sits on: the nearest ancestor of `from` that actually
 * overflows and allows scrolling, else the document. Settings pages scroll the document today; this
 * keeps the bar working if a layout ever moves the content into a scrolling slot. Only elements
 * that overflow are asked for their computed style, which keeps the walk cheap.
 */
function scrollContainerOf(from: HTMLElement | null): HTMLElement {
  const root = (document.scrollingElement as HTMLElement | null) ?? document.documentElement
  for (let el = from?.parentElement ?? null; el && el !== document.body; el = el.parentElement) {
    if (el === document.documentElement) break
    if (el.scrollHeight <= el.clientHeight) continue
    const overflowY = getComputedStyle(el).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return el
  }
  return root
}

/** How far the bar reaches up from the bottom of the viewport, final even while it slides in. */
function coveredHeight(bar: HTMLElement) {
  // The slide-in is a transform, which layout ignores: offsetHeight plus the positioner's offset is
  // where the bar ends up. Only a laid-out bar is worth a computed style (slow and useless in jsdom).
  if (bar.offsetHeight > 0) {
    const positioner = bar.parentElement
    const offset = positioner ? parseFloat(getComputedStyle(positioner).bottom) : NaN
    if (Number.isFinite(offset)) return Math.ceil(bar.offsetHeight + offset)
  }
  const rect = bar.getBoundingClientRect()
  // Not laid out yet (or jsdom): nothing to reserve.
  if (rect.height === 0) return 0
  return Math.max(0, Math.ceil(window.innerHeight - rect.top))
}

/**
 * Room in the page flow for a bar fixed to the bottom of the viewport (SIB-1113).
 *
 * Chakra's ActionBar floats over the content, so the last fields of a long form end up under it.
 * The hook measures the bar; a spacer of that height at the end of the content makes the page
 * exactly as much taller as the bar covers, and the scroll container's `scroll-padding-bottom`
 * makes the browser keep focused and scrolled-to elements clear of it.
 *
 * The field in use (its Chakra root, so a switch counts as the whole switch) is kept out from under
 * the bar with a plain `scrollIntoView({ block: 'nearest' })`, which honours that padding and does
 * nothing while the field is already visible. It runs when the bar appears or changes height and
 * when the field itself grows, e.g. by an error message below it. The page is never moved for
 * anything else, and not animated when the user prefers reduced motion.
 */
export function useFloatingBarSpacer(active: boolean) {
  const [bar, setBar] = useState<HTMLElement | null>(null)
  const [height, setHeight] = useState(0)
  const barRef = useCallback((el: HTMLElement | null) => setBar(el), [])
  const spacerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!active || !bar) {
      setHeight(0)
      return
    }

    // Found once per activation: the layout does not change while the bar is shown.
    const container = scrollContainerOf(spacerRef.current)
    let covered = 0

    /** The control in use as its Chakra root; null when nothing outside the bar has focus. */
    const fieldInUse = () => {
      const el = document.activeElement
      if (!(el instanceof HTMLElement) || el === document.body || bar.contains(el)) return null
      return el.closest<HTMLElement>('[data-scope][data-part="root"]') ?? el
    }

    const reveal = () => {
      const field = fieldInUse()
      if (!field || covered === 0) return
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      field.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' })
    }

    const measure = () => {
      const next = coveredHeight(bar)
      setHeight(next)
      container.style.scrollPaddingBottom = next > 0 ? `${next + REVEAL_GAP}px` : ''
      // Only a new height can newly cover the field; a remeasure at the same height (a resize,
      // the end of the slide-in) leaves the user's scroll position alone.
      if (next !== covered) {
        covered = next
        reveal()
      }
    }

    const hasResizeObserver = typeof ResizeObserver !== 'undefined'
    const barObserver = hasResizeObserver ? new ResizeObserver(measure) : null
    // Fires once on observe, then whenever the field in use changes size.
    const fieldObserver = hasResizeObserver ? new ResizeObserver(reveal) : null
    const watchFieldInUse = () => {
      fieldObserver?.disconnect()
      const field = fieldInUse()
      if (field) fieldObserver?.observe(field)
    }

    measure()
    watchFieldInUse()
    barObserver?.observe(bar)
    bar.addEventListener('animationend', measure)
    window.addEventListener('resize', measure)
    document.addEventListener('focusin', watchFieldInUse)
    return () => {
      barObserver?.disconnect()
      fieldObserver?.disconnect()
      bar.removeEventListener('animationend', measure)
      window.removeEventListener('resize', measure)
      document.removeEventListener('focusin', watchFieldInUse)
      container.style.scrollPaddingBottom = ''
    }
  }, [active, bar])

  return { barRef, spacerRef, height }
}
